import { Link } from "react-router-dom";

import { Glyph } from "./Glyph";

/**
 * The six places a clinic goes, as a panel on the dashboard.
 *
 * It sits in the dashboard's grid at the size every other panel gets, about
 * 490 by 317, and it is drawn for that size rather than shrunk down from
 * something larger. The supplied reference is a standalone composition on a
 * surface three times this one: what is taken from it is the language — the
 * washed tile, the icon in a tinted disc, the restrained accent, the quiet
 * depth in a corner — and not its proportions. A heading at the reference's
 * scale would leave no room for the six things the panel is for.
 *
 * So: no descriptions. At this size the icon and the name carry it, and a
 * sentence under "Find patient" explaining that it finds a patient is a line
 * of type bought at the cost of the thing it describes.
 *
 * Every route here already exists and is reached from the navigation as
 * well. Nothing here invents a destination or is the only way to one.
 */

type Action = {
  to: string;
  icon: string;
  label: string;
  tone: string;
};

/* Two columns, read across. The tone is a colour family and nothing more: it
   never carries meaning, so nobody has to learn it and a colour-blind eye
   loses nothing by not seeing it. */
const ACTIONS: Action[] = [
  { to: "/patients", icon: "search", label: "Find patient", tone: "champagne" },
  { to: "/appointments", icon: "calendar", label: "Appointments", tone: "powder" },
  { to: "/results", icon: "flask", label: "View results", tone: "sage" },
  { to: "/messages", icon: "message", label: "Needs a reply", tone: "blush" },
  { to: "/calendar", icon: "recall", label: "My calendar", tone: "lavender" },
  { to: "/poster", icon: "poster", label: "Clinic poster", tone: "cream" },
];

export function QuickActions() {
  return (
    <article className="panel quick">
      <header>
        <h2>Quick actions</h2>
      </header>
      <p className="quick-lead">Get to what you need quickly.</p>

      <div className="qa-grid">
        {ACTIONS.map(({ to, icon, label, tone }) => (
          /* The whole tile is the link. The chevron is drawn, not a control:
             a control inside a control gives a keyboard two stops for one
             destination and a mouse a small square to hit instead of a
             large one. */
          <Link key={to} className={`qa-tile qa-${tone}`} to={to}>
            <span className="qa-mark">
              <Glyph name={icon} />
            </span>
            <span className="qa-label">{label}</span>
            <span className="qa-go" aria-hidden="true">
              <Glyph name="chevron" />
            </span>
          </Link>
        ))}
      </div>
    </article>
  );
}
